import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authApi, publicApi } from '../services/api';
import { useToast } from '../components/Toast';

const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [view, setView] = useState<'landing' | 'campaign-select' | 'apply' | 'verify-code'>('landing');
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recruitmentOpen, setRecruitmentOpen] = useState<boolean | null>(null);
  const [activeCampaigns, setActiveCampaigns] = useState<any[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');

  // 공통 필드
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  // 로그인/지원자등록 필드
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  // 개인정보 동의
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [privacyRetentionAgreed, setPrivacyRetentionAgreed] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // 비밀번호 초기화 팝업
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetStep, setResetStep] = useState<'info' | 'code'>('info');
  const [resetEmail, setResetEmail] = useState('');
  const [resetName, setResetName] = useState('');
  const [resetPhone, setResetPhone] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      setView('apply');
    }
    publicApi.getRecruitmentStatus()
      .then((data) => {
        setRecruitmentOpen(data.recruitment_open);
        setActiveCampaigns(data.active_campaigns || []);
        // 캠페인 1개면 자동 선택
        if (data.active_campaigns?.length === 1) {
          setSelectedCampaignId(String(data.active_campaigns[0].id));
        }
      })
      .catch(() => setRecruitmentOpen(false));
  }, [searchParams]);

  const formatPhone = (value: string) => {
    const numbers = value.replace(/[^0-9]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const handleRegister = async () => {
    if (!name.trim()) { setError('이름을 입력해주세요.'); return; }
    const phoneNumbers = phone.replace(/[^0-9]/g, '');
    if (phoneNumbers.length < 10 || phoneNumbers.length > 11) { setError('올바른 전화번호를 입력해주세요.'); return; }
    if (!email.trim()) { setError('이메일을 입력해주세요.'); return; }
    if (!password.trim() || password.length < 6) { setError('비밀번호는 6자 이상이어야 합니다.'); return; }
    if (password !== passwordConfirm) { setError('비밀번호가 일치하지 않습니다.'); return; }
    if (activeCampaigns.length > 0 && !selectedCampaignId) { setError('지원 공고를 선택해주세요.'); return; }
    if (!privacyAgreed) { setError('개인정보 수집·이용에 동의해주세요.'); return; }

    setLoading(true);
    setError('');
    try {
      const result = await authApi.register(name, email, password, passwordConfirm, phone, selectedCampaignId ? parseInt(selectedCampaignId) : undefined, true, privacyRetentionAgreed);
      toast.success(result.message);
      setPendingEmail(email.trim());
      setVerificationCode('');
      setView('verify-code');
    } catch (err: any) {
      setError(err.response?.data?.message || '지원자등록에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim() || verificationCode.length !== 6) { setError('6자리 인증코드를 입력해주세요.'); return; }
    setLoading(true);
    setError('');
    try {
      const result = await authApi.verifyCode(pendingEmail, verificationCode);
      toast.success(result.message);
      setMode('login');
      setView('apply');
      setName('');
      setPhone('');
      setPasswordConfirm('');
      setVerificationCode('');
    } catch (err: any) {
      setError(err.response?.data?.message || '인증에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!email.trim()) { setError('이메일을 입력해주세요.'); return; }
    if (!password.trim()) { setError('비밀번호를 입력해주세요.'); return; }

    setLoading(true);
    setError('');
    try {
      const { accessToken } = await authApi.login(email, password, selectedCampaignId ? parseInt(selectedCampaignId) : undefined);
      localStorage.setItem('hire_token', accessToken);
      window.location.href = '/';
    } catch (err: any) {
      setError(err.response?.data?.message || '로그인에 실패했습니다.');
      setLoading(false);
    }
  };

  const openResetModal = () => {
    setResetEmail('');
    setResetName('');
    setResetPhone('');
    setResetCode('');
    setResetError('');
    setResetStep('info');
    setShowResetModal(true);
  };

  const handleResetPassword = async () => {
    if (!resetName.trim()) { setResetError('이름을 입력해주세요.'); return; }
    if (!resetEmail.trim()) { setResetError('이메일을 입력해주세요.'); return; }
    const phoneNumbers = resetPhone.replace(/[^0-9]/g, '');
    if (phoneNumbers.length < 10 || phoneNumbers.length > 11) { setResetError('올바른 전화번호를 입력해주세요.'); return; }

    setLoading(true);
    setResetError('');
    try {
      const result = await authApi.resetPassword(resetEmail, resetName, resetPhone);
      toast.success(result.message);
      setResetStep('code');
      setResetCode('');
    } catch (err: any) {
      setResetError(err.response?.data?.message || '비밀번호 초기화에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyResetCode = async () => {
    if (!resetCode.trim() || resetCode.length !== 6) { setResetError('6자리 인증코드를 입력해주세요.'); return; }

    setLoading(true);
    setResetError('');
    try {
      const result = await authApi.verifyResetCode(resetEmail, resetCode);
      toast.success(result.message);
      setShowResetModal(false);
    } catch (err: any) {
      setResetError(err.response?.data?.message || '인증에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode: 'login' | 'register') => {
    setMode(newMode);
    setError('');
    setPassword('');
    setPasswordConfirm('');
    setPrivacyAgreed(false);
    setPrivacyRetentionAgreed(false);
  };

  if (recruitmentOpen === null) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-sm text-slate-400">로딩 중...</div>
      </div>
    );
  }

  // ===== 랜딩 화면: 카드 선택 =====
  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-lg">
          <div className="text-center mb-10">
            <img
              src="/grk-logo.jpg"
              alt="GRK Career Management"
              className="h-14 mx-auto object-contain"
              style={{ mixBlendMode: 'multiply' }}
            />
            <p className="mt-3 text-sm text-slate-500 tracking-[0.2em] uppercase">
              Career Management
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* 입사지원 카드 */}
            {recruitmentOpen ? (
              <button
                onClick={() => {
                  if (activeCampaigns.length > 1) {
                    setView('campaign-select');
                  } else {
                    if (activeCampaigns.length === 1) setSelectedCampaignId(String(activeCampaigns[0].id));
                    setView('apply');
                  }
                }}
                className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-left hover:border-indigo-300 hover:shadow-xl transition-all group"
              >
                <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-indigo-100 transition-colors">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-base font-semibold text-slate-800 mb-1">입사지원</h3>
                {activeCampaigns.length > 0 ? (
                  <div className="text-xs text-slate-500 leading-relaxed">
                    <p className="mb-1">채용중:</p>
                    {activeCampaigns.map((c: any) => (
                      <p key={c.id} className="text-indigo-600">• {c.name}</p>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 leading-relaxed">
                    신규 지원 및<br />기존 지원자 로그인
                  </p>
                )}
              </button>
            ) : (
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-8 text-left opacity-60 cursor-not-allowed">
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-base font-semibold text-slate-400 mb-1">입사지원</h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  현재 채용이<br />비활성화 상태입니다.
                </p>
              </div>
            )}

            {/* 증명서 발급 카드 */}
            <button
              onClick={() => navigate('/document')}
              className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-left hover:border-indigo-300 hover:shadow-xl transition-all group"
            >
              <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-indigo-100 transition-colors">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-slate-800 mb-1">증명서 발급</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                재직 / 경력 / 퇴직<br />증명서 발급
              </p>
            </button>
          </div>

          <div className="mt-8 text-center">
            <p className="text-xs text-slate-400">
              문의: sungbae@grkcon.com
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ===== 캠페인 선택 화면 =====
  if (view === 'campaign-select') {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/grk-logo.jpg" alt="GRK Career Management" className="h-14 mx-auto object-contain" style={{ mixBlendMode: 'multiply' }} />
            <p className="mt-3 text-sm text-slate-500 tracking-[0.2em] uppercase">Career Management</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 md:p-10">
            <h2 className="text-xl font-semibold text-slate-800 text-center mb-2">지원 공고 선택</h2>
            <p className="text-sm text-slate-500 text-center mb-6">지원하실 채용 공고를 선택해주세요.</p>

            <div className="space-y-3">
              {activeCampaigns.map((c: any) => (
                <button
                  key={c.id}
                  onClick={() => { setSelectedCampaignId(String(c.id)); setView('apply'); }}
                  className="w-full text-left p-4 border border-slate-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50/50 transition-all group"
                >
                  <h3 className="text-sm font-semibold text-slate-800 group-hover:text-indigo-700">{c.name}</h3>
                  {c.description && <p className="text-xs text-slate-500 mt-1">{c.description}</p>}
                  <p className="text-xs text-slate-400 mt-1">
                    {c.start_date?.split('T')[0]} ~ {c.end_date?.split('T')[0]}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => { setView('landing'); setError(''); }}
            className="w-full text-center text-xs text-slate-400 hover:text-slate-600 mt-4 py-2 transition-colors"
          >
            ← 처음으로
          </button>
        </div>
      </div>
    );
  }

  // ===== 이메일 인증코드 입력 화면 =====
  if (view === 'verify-code') {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/grk-logo.jpg" alt="GRK Career Management" className="h-14 mx-auto object-contain" style={{ mixBlendMode: 'multiply' }} />
            <p className="mt-3 text-sm text-slate-500 tracking-[0.2em] uppercase">Career Management</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 md:p-10">
            <h2 className="text-xl font-semibold text-slate-800 text-center mb-2">이메일 인증</h2>
            <p className="text-sm text-slate-500 text-center mb-1">
              <span className="font-medium text-slate-700">{pendingEmail}</span>
            </p>
            <p className="text-sm text-slate-500 text-center mb-6">으로 발송된 6자리 인증코드를 입력해주세요.</p>

            {error && <p className="text-sm text-red-500 bg-red-50 rounded-lg px-4 py-2.5 mb-4">{error}</p>}

            <div onKeyDown={(e) => { if (e.key === 'Enter' && !loading) handleVerifyCode(); }}>
              <input
                type="text"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="인증코드 6자리"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-center text-2xl tracking-[8px] font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                autoFocus
              />

              <button
                onClick={handleVerifyCode}
                disabled={loading || verificationCode.length !== 6}
                className="w-full mt-4 py-3 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? '확인 중...' : '인증 완료'}
              </button>
            </div>

            <p className="text-xs text-slate-400 text-center mt-4">인증코드는 10분간 유효합니다.</p>

            <button
              onClick={() => { setView('apply'); setError(''); setVerificationCode(''); }}
              className="w-full text-center text-xs text-slate-400 hover:text-slate-600 mt-2 py-2 transition-colors"
            >
              ← 돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== 입사지원 로그인/등록 화면 =====
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/grk-logo.jpg"
            alt="GRK Career Management"
            className="h-14 mx-auto object-contain"
            style={{ mixBlendMode: 'multiply' }}
          />
          <p className="mt-3 text-sm text-slate-500 tracking-[0.2em] uppercase">
            Career Management
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 md:p-10">
          <h2 className="text-xl font-semibold text-slate-800 text-center mb-2">
            입사지원 시스템
          </h2>
          {selectedCampaignId && (
            <p className="text-xs text-indigo-600 font-medium text-center mb-1">
              {activeCampaigns.find((c: any) => String(c.id) === selectedCampaignId)?.name}
            </p>
          )}
          <p className="text-sm text-slate-500 text-center mb-8">
            {mode === 'register' ? '계정을 생성합니다.' : '본인 확인을 위해 정보를 입력해주세요.'}
          </p>

          <div className="flex mb-6 bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => switchMode('login')}
              className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${mode === 'login' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
            >
              로그인
            </button>
            <button
              onClick={() => switchMode('register')}
              className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${mode === 'register' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
            >
              지원자등록
            </button>
          </div>

          <div className="space-y-4" onKeyDown={(e) => { if (e.key === 'Enter' && !loading) { mode === 'register' ? handleRegister() : handleLogin(); } }}>
            {mode === 'register' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">이름</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="홍길동"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">전화번호</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    placeholder="010-1234-5678"
                    maxLength={13}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">이메일</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="test@example.com"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">비밀번호</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
            </div>
            {mode === 'login' && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={openResetModal}
                  className="text-xs text-indigo-500 hover:text-indigo-700 transition-colors"
                >
                  비밀번호를 잊으셨나요?
                </button>
              </div>
            )}
            {mode === 'register' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">비밀번호 확인</label>
                  <input
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    placeholder="비밀번호 확인"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* 개인정보 동의 */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacyAgreed}
                      onChange={(e) => setPrivacyAgreed(e.target.checked)}
                      className="mt-0.5 w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm text-slate-700">
                      <span className="text-red-500 font-medium">[필수]</span>{' '}
                      개인정보 수집·이용에 동의합니다.{' '}
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
                        className="text-indigo-500 hover:text-indigo-700 underline"
                      >
                        상세보기
                      </button>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacyRetentionAgreed}
                      onChange={(e) => setPrivacyRetentionAgreed(e.target.checked)}
                      className="mt-0.5 w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm text-slate-600">
                      <span className="text-slate-400 font-medium">[선택]</span>{' '}
                      불합격 시 인재풀 등록을 위한 개인정보 보관(1년)에 동의합니다.
                    </span>
                  </label>
                </div>
              </>
            )}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="pt-2">
              <button
                onClick={mode === 'register' ? handleRegister : handleLogin}
                disabled={loading}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '처리 중...' : mode === 'register' ? '지원자등록' : '로그인'}
              </button>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-slate-400">
              GRK Career Management 입사지원 전용 시스템입니다.
            </p>
          </div>
        </div>

        <button
          onClick={() => { setView(activeCampaigns.length > 1 ? 'campaign-select' : 'landing'); setError(''); }}
          className="w-full text-center text-xs text-slate-400 hover:text-slate-600 mt-4 py-2 transition-colors"
        >
          ← {activeCampaigns.length > 1 ? '공고 선택으로' : '처음으로'}
        </button>
      </div>

      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-1">비밀번호 초기화</h3>
            <p className="text-sm text-slate-500 mb-6">
              {resetStep === 'info' ? '가입 시 등록한 정보를 입력해주세요.' : `${resetEmail}으로 발송된 인증코드를 입력해주세요.`}
            </p>

            {resetStep === 'info' ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">이름</label>
                  <input type="text" value={resetName} onChange={(e) => setResetName(e.target.value)}
                    placeholder="홍길동" className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">이메일</label>
                  <input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="test@example.com" className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">전화번호</label>
                  <input type="tel" value={resetPhone} onChange={(e) => setResetPhone(formatPhone(e.target.value))}
                    placeholder="010-1234-5678" maxLength={13} className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                </div>
              </div>
            ) : (
              <div onKeyDown={(e) => { if (e.key === 'Enter' && !loading) handleVerifyResetCode(); }}>
                <input type="text" maxLength={6} value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="인증코드 6자리"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg text-center text-2xl tracking-[8px] font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  autoFocus />
                <p className="text-xs text-slate-400 text-center mt-2">인증코드는 10분간 유효합니다.</p>
              </div>
            )}

            {resetError && (
              <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg mt-3">
                <p className="text-sm text-red-600">{resetError}</p>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button onClick={() => { if (resetStep === 'code') { setResetStep('info'); setResetError(''); } else { setShowResetModal(false); } }}
                className="flex-1 py-2.5 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
                {resetStep === 'code' ? '← 돌아가기' : '취소'}
              </button>
              <button
                onClick={resetStep === 'info' ? handleResetPassword : handleVerifyResetCode}
                disabled={loading}
                className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50">
                {loading ? '처리 중...' : resetStep === 'info' ? '인증코드 발송' : '인증 완료'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">개인정보 수집·이용 동의서</h3>
            </div>
            <div className="p-6 overflow-y-auto text-sm text-slate-700 space-y-4 leading-relaxed">
              <p className="font-semibold text-slate-800">주식회사 지알케이컨설팅(이하 '회사')은 채용 전형을 위해 아래와 같이 개인정보를 수집·이용합니다.</p>

              <div>
                <h4 className="font-semibold text-slate-800 mb-1">1. 수집 목적</h4>
                <p>채용 전형 진행 (서류심사, 서면평가, 면접, 합격자 통보, 입사 행정 처리)</p>
              </div>

              <div>
                <h4 className="font-semibold text-slate-800 mb-1">2. 수집 항목</h4>
                <table className="w-full border border-slate-200 text-xs">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="border border-slate-200 px-3 py-2 text-left">구분</th>
                      <th className="border border-slate-200 px-3 py-2 text-left">항목</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-slate-200 px-3 py-2 font-medium">필수</td>
                      <td className="border border-slate-200 px-3 py-2">이름, 이메일, 전화번호, 비밀번호(암호화)</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-3 py-2 font-medium">지원서</td>
                      <td className="border border-slate-200 px-3 py-2">이력서, 자기소개서, 희망 포지션, 희망 연봉</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-3 py-2 font-medium">입사 시</td>
                      <td className="border border-slate-200 px-3 py-2">주민등록번호, 통장사본, 졸업증명서, 경력증명서, 증명사진 등</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <h4 className="font-semibold text-slate-800 mb-1">3. 보유 및 이용 기간</h4>
                <table className="w-full border border-slate-200 text-xs">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="border border-slate-200 px-3 py-2 text-left">대상</th>
                      <th className="border border-slate-200 px-3 py-2 text-left">보관 기간</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-slate-200 px-3 py-2">합격자 (입사)</td>
                      <td className="border border-slate-200 px-3 py-2">근로관계 종료 후 3년 (근로기준법 제42조)</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-3 py-2">불합격자</td>
                      <td className="border border-slate-200 px-3 py-2">채용 전형 종료 후 즉시 파기</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-3 py-2">인재풀 동의자</td>
                      <td className="border border-slate-200 px-3 py-2">동의일로부터 1년 후 파기</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <h4 className="font-semibold text-slate-800 mb-1">4. 동의 거부 권리</h4>
                <p>귀하는 개인정보 수집·이용에 대한 동의를 거부할 권리가 있습니다. 다만, 필수 항목에 대한 동의를 거부하실 경우 채용 전형 진행이 불가합니다.</p>
              </div>

              <div>
                <h4 className="font-semibold text-slate-800 mb-1">5. 개인정보 처리 위탁</h4>
                <p>회사는 채용 전형 진행을 위해 AI 기반 서류심사 서비스를 이용하며, 이 과정에서 이력서 및 자기소개서가 처리될 수 있습니다.</p>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200">
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="w-full py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
